import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LiliButton, LiliToast, LiliToastDefaultConfig } from '@lili/lili-frontend-common';
import { MONEY_MOVE } from '../../constants';
import { useStore } from '../../../../store';
import {
  addDotsWithNullIfNeeded,
  convertIntToFloatStr,
  currencyInputValueToNumber,
  formatAmount,
  handleInputWithCurrencyOnChange,
} from '../../../../helpers/currency.helper';
import { useMoveFromTaxBucketQuery } from '../../../../hooks/query/useMoveFromTaxBucketQuery';
import { CURRENCY, CURRENCY_USD_SIGN, ENTER_KEY, FORMAT_TYPE } from '../../../../constants/general';
import InputWithCurrency from '../../../../generic-components/InputWithCurrency';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { useUploadGenericFile } from '../../../../hooks/utilities/useUploadGenericFile';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../../types';
import { VIEWS_MAP } from '../../../../router/constants';

interface IProps {
  pullFromTaxBucket: boolean;
}

const TaxBucketMoneyMove = ({ pullFromTaxBucket }: IProps) => {
  const { t } = useTranslation();
  const summary = useStore((state) => state.summary);
  const { getPrimaryAccount } = useStore((state) => state);
  const CHECKING_BALANCE = getPrimaryAccount().balance;
  const TAX_BUCKET_BALANCE =
    summary && summary.taxBucketBalance && summary.taxBucketBalance.balance !== undefined
      ? summary.taxBucketBalance.balance
      : 0;
  const { HEADER, TEXT, BUTTON_TEXT, ERROR_MESSAGE } = MONEY_MOVE;
  const navigate = useNavigate();
  const INITIAL_INPUT_VALUE = '';
  const NULL_INPUT_VALUE = '0';
  const NULL_DOT_INPUT_VALUE = '0.';
  const [inputValue, setInputValue] = useState<string>(INITIAL_INPUT_VALUE);
  const [loading, setLoading] = useState<boolean>(false);

  const { customerId } = useStore((state) => state.currUser());
  const { mutateAsync: postUploadSnapShot, isLoading } = useUploadGenericFile();
  const { refetch: sendMoney } = useMoveFromTaxBucketQuery({
    customerId,
    amount: parseFloat(inputValue.replaceAll(',', '').replaceAll('$', '')),
    pullFromTaxBucket,
    onSuccess: () => {
      if (pullFromTaxBucket) {
        trackMixPanel('Withdrawal tax jar fund', {
          Origin: 'Tax Jar Feed',
        });
      } else {
        trackMixPanel('Add tax jar fund', {
          Origin: 'Tax Jar Feed',
        });
      }
      toast(
        <LiliToast
          id='upperMessageContent'
          text={
            pullFromTaxBucket
              ? t('TAX_BUCKET_WITHDRAW_SUCCESS_POPUP_MESSAGE', { amount: addDotsWithNullIfNeeded(inputValue) })
              : t('TAX_BUCKET_ADD_SUCCESS_POPUP_MESSAGE', { amount: addDotsWithNullIfNeeded(inputValue) })
          }
        />,
        {
          ...LiliToastDefaultConfig,
        },
      );
      navigate(`/${VIEWS_MAP.TAX_BUCKET_DASHBOARD}`);
    },
  });

  const handleOnChange = (event: { target: { value: string } }) => {
    handleInputWithCurrencyOnChange(event, setInputValue);
  };

  const isButtonDisabled = () => {
    if (inputValue === INITIAL_INPUT_VALUE || inputValue === NULL_INPUT_VALUE || inputValue === NULL_DOT_INPUT_VALUE) {
      return true;
    }
    if (summary) {
      if (pullFromTaxBucket) {
        return currencyInputValueToNumber(inputValue) > TAX_BUCKET_BALANCE;
      }
      return currencyInputValueToNumber(inputValue) > CHECKING_BALANCE;
    }
    return true;
  };

  const showErrorMessage = () => {
    if (summary) {
      if (pullFromTaxBucket) {
        return currencyInputValueToNumber(inputValue) > TAX_BUCKET_BALANCE;
      }
      return currencyInputValueToNumber(inputValue) > CHECKING_BALANCE;
    }
    return true;
  };

  const handleOnClick = async () => {
    await postUploadSnapShot({
      fileUsageType: GENERIC_FILE_UPLOAD_TYPES.TAX_BUCKET_AMOUNT,
      imageName: `${Date.now()}`,
    });
    sendMoney();
    setLoading(true);
  };

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  });

  return (
    <div className='h-[70dvh] flex flex-col w-full justify-center shadow-widgetCard border border-outline-primary-bw-03 bg-surface-primary-bw-00 rounded-[12px]'>
      <span className='font-metricMedium text-center text-lg leading-[1.5rem] text-bw08 mb-2'>
        {t(pullFromTaxBucket ? HEADER.WITHDRAW : HEADER.ADD)}
      </span>
      <span className='font-metric text-center text-sm leading-[1rem] text-bw07 px-[4rem] mb-[2.625rem]'>
        <span className='text-action03 font-metricMedium text-lg'>
          {pullFromTaxBucket
            ? `${formatAmount(convertIntToFloatStr(TAX_BUCKET_BALANCE), FORMAT_TYPE.FORMATTED, CURRENCY.USD.SYMBOL, false)}`
            : `${formatAmount(convertIntToFloatStr(CHECKING_BALANCE), FORMAT_TYPE.FORMATTED, CURRENCY.USD.SYMBOL, false)}`}
        </span>
        {t(pullFromTaxBucket ? TEXT.WITHDRAW : TEXT.ADD)}
      </span>
      <div className='flex w-full h-[250px] justify-between items-center flex-col'>
        <div className='flex w-full justify-center items-center flex-col'>
          <InputWithCurrency
            containerClassName=''
            currency={CURRENCY_USD_SIGN}
            currencyClassName='font-metricMedium text-base text-bw07'
            id='TaxBucketMoneyMovement'
            inputCLassName={`border rounded-md focus:outline-none w-[17.5rem] h-[4rem] caret-action03 text-center font-metricMedium text-2xl ${showErrorMessage() ? 'border-red02' : 'border-action03'}`}
            onChange={handleOnChange}
            value={inputValue}
            inputRef={inputRef as React.Ref<HTMLInputElement>}
            eventKey={ENTER_KEY}
            handleKeyDown={() => {
              if (!isButtonDisabled()) handleOnClick();
            }}
          />
          {showErrorMessage() && (
            <span className='text-center font-metric text-xxs text-red02 pt-[8px]'>
              {t(pullFromTaxBucket ? ERROR_MESSAGE.WITHDRAW : ERROR_MESSAGE.ADD)}
            </span>
          )}
        </div>
        <LiliButton
          id='moneyMove'
          onClick={handleOnClick}
          disabled={isButtonDisabled()}
          className='w-[17.5rem] h-[3.125rem]'
          title={t(pullFromTaxBucket ? BUTTON_TEXT.WITHDRAW : BUTTON_TEXT.ADD)}
          isLoading={loading || isLoading}
        />
      </div>
    </div>
  );
};

export default TaxBucketMoneyMove;
